var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { DataFrame } from '../../model/DataFrame';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { CommonPlaceholders } from '../../localization/helpers.js';
import '../icon/Icon.js';
let BasicTable = class BasicTable extends LitElement {
    constructor() {
        super(...arguments);
        this.rowsPerPage = 10;
        this.selectedRowsIndex = [];
        this.selectType = 'single';
        this.bare = false;
        // --------------------------
        // State
        // --------------------------
        this.searchQuery = '';
        this.sortColumn = null;
        this.sortDirection = 'asc';
        this.currentPage = 1;
    }
    // --------------------------
    // Handlers
    // --------------------------
    handleSearch(event) {
        const input = event.target;
        this.searchQuery = input.value.toLowerCase();
        this.currentPage = 1;
    }
    handleSort(column) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        }
        else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
    }
    handleRowSelection(rowIndex) {
        // Ensure the DataFrame and index_column are defined
        if (!this.dataFrame || !this.dataFrame.index_column) {
            console.error('DataFrame or index_column is not defined.');
            return;
        }
        // Get the row from the paginated rows
        const row = this.dataFrame.selectRowIndex(rowIndex);
        if (!row) {
            console.error('Row not found for the given index:', rowIndex);
            return;
        }
        // Get the row's unique identifier using the index_column
        const rowId = row[this.dataFrame.index_column];
        if (rowId === undefined) {
            console.error(`Row does not contain the index_column "${this.dataFrame.index_column}".`, row);
            return;
        }
        // Handle selection logic
        if (this.selectType === 'none')
            return;
        if (this.selectType === 'single') {
            if (this.selectedRowsIndex.includes(rowId)) {
                this.selectedRowsIndex = []; // Assign a new array reference
            }
            else {
                this.selectedRowsIndex = [rowId]; // Assign a new array reference
            }
        }
        else if (this.selectType === 'multiple') {
            if (this.selectedRowsIndex.includes(rowId)) {
                this.selectedRowsIndex = this.selectedRowsIndex.filter((id) => id !== rowId); // Assign a new array reference
            }
            else {
                this.selectedRowsIndex = [...this.selectedRowsIndex, rowId]; // Assign a new array reference
            }
        }
        // Dispatch event
        this.dispatchEvent(new CustomEvent('rows-selected', {
            detail: {
                selectedRows: this.dataFrame.selectRowsIndex(this.selectedRowsIndex),
            },
            bubbles: true,
            composed: true,
        }));
    }
    handleRowDblClick(row) {
        this.dispatchEvent(new CustomEvent('row-dblclick', {
            detail: { row },
            bubbles: true,
            composed: true,
        }));
    }
    // --------------------------
    // Computed Data
    // --------------------------
    get visibleColumns() {
        return this.dataFrame.columns.filter((col) => !col.id);
        // return this.dataFrame.columns;
    }
    get allRows() {
        return this.dataFrame ? this.dataFrame.rows : [];
    }
    get filteredRows() {
        if (!this.searchQuery)
            return this.allRows;
        return this.allRows.filter((row) => this.dataFrame.columns.some((col) => {
            const val = row[col.name];
            return (val != null && String(val).toLowerCase().includes(this.searchQuery));
        }));
    }
    get sortedRows() {
        const rows = [...this.filteredRows];
        if (this.sortColumn) {
            rows.sort((a, b) => {
                const valA = a[this.sortColumn];
                const valB = b[this.sortColumn];
                if (valA == null && valB == null)
                    return 0;
                if (valA == null)
                    return this.sortDirection === 'asc' ? -1 : 1;
                if (valB == null)
                    return this.sortDirection === 'asc' ? 1 : -1;
                return this.sortDirection === 'asc'
                    ? valA < valB
                        ? -1
                        : valA > valB
                            ? 1
                            : 0
                    : valA < valB
                        ? 1
                        : valA > valB
                            ? -1
                            : 0;
            });
        }
        return rows;
    }
    get paginatedRows() {
        if (this.bare)
            return this.sortedRows;
        const start = (this.currentPage - 1) * this.rowsPerPage;
        return this.sortedRows.slice(start, start + this.rowsPerPage);
    }
    get totalPages() {
        return Math.max(1, Math.ceil(this.sortedRows.length / this.rowsPerPage));
    }
    goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
        }
    }
    // --------------------------
    // Render
    // --------------------------
    _renderToolbar() {
        return html `
      <div class="header">
        <div class="row-count">
          ${msg('Number of rows:', { id: 'table.rowCount', desc: 'Row count label' })} ${this.sortedRows.length}
        </div>
        <div class="search-container">
          <slot name="table-actions"></slot>
          <input
            class="search-bar"
            type="text"
            placeholder=${CommonPlaceholders.search()}
            @input=${this.handleSearch}
          />
          <div class="pagination">
            <button
              ?disabled=${this.currentPage === 1}
              @click=${() => this.goToPage(this.currentPage - 1)}
            >
              <basic-icon name="back" size="16" .label=${msg('Previous', { id: 'table.pagination.previous', desc: 'Previous page button' })}></basic-icon>
            </button>
            <span class="page-indicator">
              ${msg('Page', { id: 'table.pagination.page', desc: 'Page label' })} ${this.currentPage} / ${this.totalPages}
            </span>
            <button
              ?disabled=${this.currentPage === this.totalPages}
              @click=${() => this.goToPage(this.currentPage + 1)}
            >
              <basic-icon name="next" size="16" .label=${msg('Next', { id: 'table.pagination.next', desc: 'Next page button' })}></basic-icon>
            </button>
          </div>
        </div>
      </div>
    `;
    }
    _renderTable() {
        return html `
      <div class="table-container">
        <table>
          <thead>
            <tr>
              ${this.visibleColumns.map((col) => html `
                  <th @click=${() => this.handleSort(col.name)}>
                    ${col.alias}
                    ${this.sortColumn === col.name
            ? this.sortDirection === 'asc'
                ? '▲'
                : '▼'
            : ''}
                  </th>
                `)}
            </tr>
          </thead>
          <tbody>
            ${this.paginatedRows.map((row) => {
            const rowId = row[this.dataFrame.index_column];
            const isSelected = Array.isArray(this.selectedRowsIndex) &&
                this.selectedRowsIndex.includes(rowId);
            return html `
                <tr
                  class=${isSelected ? 'selected' : ''}
                  @click=${() => this.handleRowSelection(rowId)}
                  @dblclick=${() => this.handleRowDblClick(row)}
                >
                  ${this.visibleColumns.map((col) => {
                const cellValue = row[col.name];
                return html `
                      <td>
                        ${col.type === 'html' && typeof cellValue === 'string'
                    ? unsafeHTML(cellValue)
                    : (cellValue ?? '-')}
                      </td>
                    `;
            })}
                </tr>
              `;
        })}
          </tbody>
        </table>
      </div>
    `;
    }
    render() {
        if (!this.dataFrame) {
            const msgText = msg('No DataFrame provided to the table', { id: 'table.error.noDataFrame', desc: 'No DataFrame error' });
            return this.bare
                ? html `<div class="empty">${msgText}</div>`
                : html `<basic-card class="empty">${msgText}</basic-card>`;
        }
        if (!this.allRows.length) {
            const msgText = msg('No data in this table', { id: 'table.error.noData', desc: 'No data in table error' });
            return this.bare
                ? html `<div class="empty">${msgText}</div>`
                : html `<basic-card class="empty">${msgText}</basic-card>`;
        }
        if (this.bare) {
            return this._renderTable();
        }
        return html `
      <basic-card>
        ${this._renderToolbar()}
        ${this._renderTable()}
      </basic-card>
    `;
    }
};
// --------------------------
// Styles
// --------------------------
BasicTable.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    :host([bare]) {
      height: auto;
    }

    :host([bare]) .table-container {
      flex: none;
    }

    basic-card {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 16px;
      box-sizing: border-box;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }

    .row-count {
      font-size: 13px;
      color: #666;
    }

    .search-container {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .search-bar {
      padding: 6px 10px;
      font-size: 13px;
      border: 1px solid #888;
      border-radius: 8px;
      width: 200px;
      background: #f9f9f9;
      color: #333;
    }

    .search-bar::placeholder {
      color: #999;
    }

    .pagination {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .pagination button {
      padding: 4px 8px;
      border: 1px solid #888;
      border-radius: 4px;
      background: #f9f9f9;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pagination span.page-indicator {
      font-size: 13px;
    }

    .table-container {
      flex: 1;
      overflow: auto;
    }

    .table-container::-webkit-scrollbar {
      height: 8px;
      background: #f0f0f0;
    }
    .table-container::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 4px;
    }
    .table-container::-webkit-scrollbar-thumb:hover {
      background: #aaa;
    }
    .table-container {
      scrollbar-width: thin;
      scrollbar-color: #ccc #f0f0f0;
    }

    table {
      width: max-content;
      min-width: 100%;
      border-collapse: collapse;
      table-layout: auto;
    }

    th,
    td {
      padding: 8px;
      font-size: 13px;
      text-align: left;
      border: 1px solid #ccc;
      max-width: 150px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    th {
      background: #f2f2f2;
      font-weight: bold;
      cursor: pointer;
      user-select: none;
    }

    tr:nth-child(even) {
      background: #fafafa;
    }

    tr:hover {
      background: #e6f7ff;
    }

    tr.selected {
      background: #cce5ff;
    }

    .empty {
      padding: 20px;
      text-align: center;
      color: #777;
    }
  `;
__decorate([
    property({ type: Object })
], BasicTable.prototype, "dataFrame", void 0);
__decorate([
    property({ type: Number })
], BasicTable.prototype, "rowsPerPage", void 0);
__decorate([
    property({ type: Number })
], BasicTable.prototype, "selectedRowsIndex", void 0);
__decorate([
    property({ type: String })
], BasicTable.prototype, "selectType", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicTable.prototype, "bare", void 0);
__decorate([
    state()
], BasicTable.prototype, "searchQuery", void 0);
__decorate([
    state()
], BasicTable.prototype, "sortColumn", void 0);
__decorate([
    state()
], BasicTable.prototype, "sortDirection", void 0);
__decorate([
    state()
], BasicTable.prototype, "currentPage", void 0);
BasicTable = __decorate([
    localized(),
    customElement('basic-table')
], BasicTable);
export { BasicTable };
//# sourceMappingURL=BasicTable.js.map